import SolutionsGrid from "@/components/site/SolutionsGrid";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { breadcrumbSchema, faqSchema, organizationSchema } from "@/lib/structuredData";
import {
  AlertTriangle,
  Clock,
  ShieldAlert,
  HelpCircle,
  Network,
  Check,
  MessageCircle,
  Bell,
  Sparkles,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import StatsBar from "@/components/site/StatsBar";
import { BenefitIcon } from "@/components/site/VectorMarks";

export type UseCaseBenefit = { title: string; description: string };
export type UseCaseFaq = { question: string; answer: string };

export type UseCasePageProps = {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  title: string;
  heroDescription: string;
  whatItIs: string;
  heroIcon: LucideIcon;
  challenges: string[];
  solutions: string[];
  benefits: UseCaseBenefit[];
  audiences: string[];
  faqs: UseCaseFaq[];
  ctaTagline: string;
};

const challengeIcons = [AlertTriangle, Clock, ShieldAlert, HelpCircle, Network];
const challengeIconGradients = [
  "from-pink-500 to-rose-500",
  "from-amber-400 to-orange-500",
  "from-emerald-400 to-teal-500",
  "from-sky-400 to-indigo-500",
  "from-fuchsia-500 to-violet-600",
];

const UseCasePage = ({
  slug,
  metaTitle,
  metaDescription,
  title,
  heroDescription,
  whatItIs,
  heroIcon: HeroIcon,
  challenges,
  solutions,
  benefits,
  audiences,
  faqs,
  ctaTagline,
}: UseCasePageProps) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const url = `https://yootelco.com/use-cases/${slug}`;
  const faqJsonLd = faqSchema(faqs);
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Use Cases", path: "/use-cases" },
    { name: title, path: `/use-cases/${slug}` },
  ]);

  return (
    <main className="min-h-screen bg-white">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[900px] text-center">
            <h1 className="font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px]">
              {title}
            </h1>
            <p className="mx-auto mt-[18px] max-w-[680px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px]">
              {heroDescription}
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-shell pt-[20px]">
        <div className="mx-auto grid max-w-[1120px] items-center gap-12 lg:grid-cols-2">
          <div className="flex justify-center">
            <div className="relative h-[460px] w-[244px] rounded-[44px] border-[6px] border-foreground bg-gradient-to-b from-secondary/60 via-card to-card shadow-lift">
              {/* Notch */}
              <div className="mx-auto mt-[10px] h-[22px] w-[92px] rounded-full bg-foreground" />
              {/* Status bar */}
              <div className="mt-3 flex items-center justify-between px-4 text-[9px] font-bold text-foreground">
                <span>9:41</span>
                <div className="flex items-center gap-1">
                  <span className="h-[6px] w-[6px] rounded-full bg-emerald-500" />
                  <span className="h-[6px] w-[14px] rounded-[2px] bg-foreground/80" />
                </div>
              </div>
              {/* Hero icon tile */}
              <div className="mx-auto mt-[14px] flex h-[72px] w-[72px] items-center justify-center rounded-[20px] bg-gradient-to-br from-indigo-500 via-violet-600 to-fuchsia-500 shadow-lift">
                <HeroIcon className="h-9 w-9" strokeWidth={2.2} />
              </div>
              {/* Mini colorful icon row */}
              <div className="mx-auto mt-3 flex items-center justify-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-gradient-to-br from-emerald-400 to-teal-500">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-gradient-to-br from-amber-400 to-orange-500">
                  <Zap className="h-3.5 w-3.5" strokeWidth={2.6} />
                </span>
                <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-gradient-to-br from-pink-500 to-rose-500">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
                </span>
              </div>
              {/* Chat thread */}
              <div className="absolute bottom-[18px] left-[14px] right-[14px] space-y-2">
                <div className="flex items-end gap-1.5">
                  <span className="h-5 w-5 flex-none rounded-full bg-gradient-to-br from-sky-400 to-indigo-500" />
                  <div className="max-w-[78%] rounded-[12px] rounded-bl-[4px] border border-border bg-card px-2.5 py-1.5 shadow-soft">
                    <div className="h-[6px] w-[80px] rounded-full bg-foreground/70" />
                    <div className="mt-1 h-[6px] w-[60px] rounded-full bg-muted-foreground/50" />
                  </div>
                </div>
                <div className="flex items-end justify-end gap-1.5">
                  <div className="max-w-[78%] rounded-[12px] rounded-br-[4px] bg-gradient-to-br from-indigo-500 to-violet-600 px-2.5 py-1.5 shadow-soft">
                    <div className="h-[6px] w-[70px] rounded-full bg-white/90" />
                    <div className="mt-1 h-[6px] w-[44px] rounded-full bg-white/60" />
                  </div>
                </div>
                <div className="flex items-end gap-1.5">
                  <span className="h-5 w-5 flex-none rounded-full bg-gradient-to-br from-emerald-400 to-teal-500" />
                  <div className="rounded-[12px] rounded-bl-[4px] border border-border bg-card px-2.5 py-1.5 shadow-soft">
                    <div className="h-[6px] w-[50px] rounded-full bg-foreground/70" />
                  </div>
                </div>
              </div>
              {/* Floating notification badge */}
              <span className="absolute -right-3 top-[110px] flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-pink-600 shadow-lift">
                <Bell className="h-4 w-4" strokeWidth={2.4} />
              </span>
            </div>
          </div>
          <div>
            <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[32px]">How It Works</h2>
            <p className="mt-[16px] max-w-[480px] text-[14.5px] leading-[1.65] text-muted-foreground">{whatItIs}</p>
          </div>
        </div>
      </section>

      {/* Common Challenges */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Common Challenges</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1120px] flex-wrap items-stretch justify-center gap-4">
          {challenges.map((text, i) => {
            const Icon = challengeIcons[i % challengeIcons.length];
            const gradient = challengeIconGradients[i % challengeIconGradients.length];
            return (
              <article
                key={text}
                className="flex h-full w-full items-start gap-3 rounded-[14px] border border-border/70 bg-secondary/40 px-[18px] py-[16px] sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
              >
                <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-[10px] ${gradient}`}>
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <p className="text-[13.5px] font-semibold leading-[1.45] text-foreground">{text}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* How Yootelco Solves It */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">How Yootelco Solves It</h2>
        </div>
        <SolutionsGrid items={solutions} />
      </section>

      {/* Key Benefits */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Key Benefits</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1080px] flex-wrap justify-center gap-5">
          {benefits.map((b, i) => (
            <article key={b.title} className="w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.834rem)] rounded-[16px] border border-border/70 bg-secondary/40 p-[24px] text-center">
              <BenefitIcon index={i} />
              <h3 className="mt-[14px] text-[17px] font-bold text-foreground">{b.title}</h3>
              <p className="mx-auto mt-[8px] max-w-[260px] text-[13px] leading-[1.55] text-muted-foreground">{b.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Who It's For */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto grid max-w-[1120px] items-start gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[32px]">Who It's For</h2>
            <ul className="mt-[18px] space-y-[14px]">
              {audiences.map((a) => (
                <li key={a} className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-foreground">
                  <span className="mt-[8px] h-[9px] w-[9px] flex-none rounded-full bg-primary" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-center">
            <div className="relative h-[460px] w-[244px] rounded-[44px] border-[6px] border-foreground bg-gradient-to-b from-secondary/60 via-card to-card shadow-lift">
              <div className="mx-auto mt-[10px] h-[22px] w-[92px] rounded-full bg-foreground" />
              <div className="mt-3 flex items-center justify-between px-4 text-[9px] font-bold text-foreground">
                <span>9:41</span>
                <div className="flex items-center gap-1">
                  <span className="h-[6px] w-[6px] rounded-full bg-emerald-500" />
                  <span className="h-[6px] w-[14px] rounded-[2px] bg-foreground/80" />
                </div>
              </div>
              {/* Header card */}
              <div className="mx-3 mt-3 flex items-center gap-2 rounded-[12px] bg-gradient-to-r from-sky-500 to-indigo-600 px-2.5 py-2 shadow-soft">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
                  <MessageCircle className="h-3.5 w-3.5" strokeWidth={2.4} />
                </span>
                <div className="flex-1">
                  <div className="h-[6px] w-[70%] rounded-full bg-white/90" />
                  <div className="mt-1 h-[5px] w-[40%] rounded-full bg-white/60" />
                </div>
              </div>
              {/* Chat bubbles */}
              <div className="mt-3 space-y-2 px-3">
                <div className="flex items-end gap-1.5">
                  <span className="h-5 w-5 flex-none rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-600" />
                  <div className="rounded-[12px] rounded-bl-[4px] border border-border bg-card px-2.5 py-1.5 shadow-soft">
                    <div className="h-[6px] w-[90px] rounded-full bg-foreground/70" />
                    <div className="mt-1 h-[6px] w-[60px] rounded-full bg-muted-foreground/50" />
                  </div>
                </div>
                <div className="flex items-end justify-end gap-1.5">
                  <div className="rounded-[12px] rounded-br-[4px] bg-gradient-to-br from-emerald-500 to-teal-600 px-2.5 py-1.5 shadow-soft">
                    <div className="h-[6px] w-[70px] rounded-full bg-white/90" />
                  </div>
                </div>
                <div className="flex items-end gap-1.5">
                  <span className="h-5 w-5 flex-none rounded-full bg-gradient-to-br from-amber-400 to-orange-500" />
                  <div className="rounded-[12px] rounded-bl-[4px] border border-border bg-card px-2.5 py-1.5 shadow-soft">
                    <div className="h-[6px] w-[80px] rounded-full bg-foreground/70" />
                    <div className="mt-1 h-[6px] w-[40px] rounded-full bg-muted-foreground/50" />
                  </div>
                </div>
              </div>
              {/* Bottom icon dock */}
              <div className="absolute bottom-[16px] left-[14px] right-[14px] flex items-center justify-around rounded-[14px] border border-border bg-card/90 px-2 py-2 shadow-soft backdrop-blur">
                <span className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-gradient-to-br from-pink-500 to-rose-500">
                  <Sparkles className="h-4 w-4" strokeWidth={2.4} />
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-gradient-to-br from-amber-400 to-orange-500">
                  <Zap className="h-4 w-4" strokeWidth={2.4} />
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-gradient-to-br from-emerald-400 to-teal-500">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
              </div>
              {/* Floating notification */}
              <span className="absolute -right-3 top-[90px] flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 shadow-lift">
                <Bell className="h-4 w-4" strokeWidth={2.4} />
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">4</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <StatsBar />

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
            <CTASection
        title={<><span className="text-sky">Ready</span> to get started?</>}
        description={ctaTagline}
        useCase={title}
      />

      <SiteFooter />
    </main>
  );
};

export default UseCasePage;